import React, { useEffect, useRef, useState } from 'react';
import {
  CafClusterSample, CafCorpusEntry, CafFinalResponseStance, FromTaskPayload, getCafCorpusEntry, getEmailPreview, recordCorpusFromTask,
  recordCorpusFromTaskGuidance, recordCafDecisionReview,
} from '../../../api/cafCorpus';
import { ExampleLinks, getAiSwitch, getExampleLinks, setExampleWorkflow } from '../../../api/cafAutomation';
import { FIRST_LABEL_REASON } from '../copy';
import { LabelDraft, LabelForm, draftFromEntry, draftIsValid, draftToWrite, emptyDraft, stanceField, useClaimFieldOptions } from '../LabelForm';
import { useFinalResponseSupported } from '../finalResponseSupport';
import { senderPartition } from '../lenders';
import { Modal } from '../Overlay';
import { ReadBack, finishPublication, MSG_NOT_CONFIRMED, newIdempotencyKey, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { ConflictWarnings, useConflictGate } from './ConflictWarnings';
import { EmailPicker, EmailPreviewPane } from './EmailPicker';
import { SenderOption } from './senderOptions';
import { matchesReplacementGuidance, ReplacementGuidance } from './guidanceComparison';
import { nudgeFinish } from './useCurrentEntry';

interface Props {
  senders: SenderOption[];
  preset?: { taskId: string; sender: string | null; subject?: string | null; finalResponseStance?: CafFinalResponseStance };
  onClose: () => void;
  onSaved: (entry: CafCorpusEntry | null) => void;
  onShowExample?: (entryId: string, sender: string) => void;
  correction?: { decisionId: string; reviewRevision: number; feedbackOnly?: boolean };
  canAutomate?: boolean;
}

type Existing = { state: 'none' } | { state: 'loading' } | { state: 'found'; entry: CafCorpusEntry };
type CorrectionStage = 'initial' | 'review' | 'guidance' | 'link';
type FrozenCorrection = { review: Parameters<typeof recordCafDecisionReview>[1]; guidance: FromTaskPayload;
  expectedGuidance: ReplacementGuidance; workflow: string | null; linkRevision: number; explicitLink: boolean;
  wasExisting: boolean; feedbackOnly: boolean };

const statusOf = (err: unknown) => (err as { response?: { status?: number } })?.response?.status;

/**
 * Ryan's ruling: teach the AI from one email, live, without a group. Two
 * entry points share this modal -- "Add one email as an example" (no
 * preset, staff pick the sender and email here) and decision correction
 * (preset and exact decision identity supplied).
 *
 * The backend write (`POST /caf-corpus/from-task`) is keyed on the single
 * `email_task_id`, so a repeat on the same email amends that example --
 * never labelled "guidance"/"corpus" on screen, just "approved example".
 */
export const OneEmailModal: React.FC<Props> = ({ senders, preset, onClose, onSaved, onShowExample, correction, canAutomate = false }) => {
  const toast = useToast();
  const gate = useConflictGate();
  // Final fix wave C1/C2: an older backend neither asks for nor takes the final-response setting.
  const rules = { finalResponse: useFinalResponseSupported() };
  const claimFields = useClaimFieldOptions();
  const [sender, setSender] = useState(preset?.sender ?? '');
  const [picked, setPicked] = useState<string[]>(preset ? [preset.taskId] : []);
  const [presetPreview, setPresetPreview] = useState<CafClusterSample | null>(null);
  const [existing, setExisting] = useState<Existing>({ state: 'none' });
  const [draft, setDraft] = useState<LabelDraft>(() => {
    const stance = preset?.finalResponseStance;
    return stance ? { ...emptyDraft(), finalResponse: stance } : emptyDraft();
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const [links, setLinks] = useState<ExampleLinks | null>(null);
  const [linksFailed, setLinksFailed] = useState(false);
  const [choice, setChoice] = useState<string | null>(null);
  const [stage, setStage] = useState<CorrectionStage>('initial');
  const [uncertain, setUncertain] = useState<'guidance' | 'link' | null>(null);
  const [reviewUncertain, setReviewUncertain] = useState(false);
  const [linkConflict, setLinkConflict] = useState(false);
  const [entryConflict, setEntryConflict] = useState(false);
  const savedEntry = useRef<CafCorpusEntry | null>(null);
  const frozen = useRef<FrozenCorrection | null>(null);
  const taskId = picked[0] ?? null;
  const partition = senderPartition(sender);
  // R10 (spec §4.12): the Lender lane opens this form right after staff confirm or reject a final
  // response. That verdict pre-fills the setting and wins over an existing example's stored one.
  const presetStance = preset?.finalResponseStance;

  useEffect(() => {
    if (!correction || (!saving && stage === 'initial' && !reviewUncertain)) return undefined;
    const warnBeforeReload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warnBeforeReload);
    return () => window.removeEventListener('beforeunload', warnBeforeReload);
  }, [correction, saving, stage, reviewUncertain]);

  useEffect(() => {
    if (!correction) return undefined;
    let alive = true;
    getExampleLinks().then((value) => { if (alive) setLinks(value); })
      .catch(() => { if (alive) setLinksFailed(true); });
    return () => { alive = false; };
  }, [correction]);

  useEffect(() => {
    if (!preset) return undefined;
    let alive = true;
    getEmailPreview(preset.taskId).then((value) => { if (alive) setPresetPreview(value); }).catch(() => undefined);
    return () => { alive = false; };
  }, [preset]);

  useEffect(() => {
    if (!taskId) {
      setExisting({ state: 'none' });
      return undefined;
    }
    let alive = true;
    setExisting({ state: 'loading' });
    getCafCorpusEntry(taskId, partition)
      .then((entry) => {
        if (!alive) return;
        setExisting({ state: 'found', entry });
        setDraft(presetStance ? { ...draftFromEntry(entry), finalResponse: presetStance } : draftFromEntry(entry));
        // Controller ruling (Task 9 review, carried into Task 17): nudge a
        // stuck-"partial" one-email override once, silently (see
        // `nudgeFinish`'s doc comment). Never blocks the form, never touches
        // the draft -- only the internal "found" entry is refreshed.
        if (!correction) nudgeFinish(entry).then((fresh) => { if (alive && fresh) setExisting({ state: 'found', entry: fresh }); });
      })
      .catch((err) => {
        if (!alive) return;
        if (correction && statusOf(err) !== 404) {
          setProblem("Couldn't check this email's example. Try again before teaching.");
          setExisting({ state: 'loading' });
          return;
        }
        setExisting({ state: 'none' });
        setDraft(presetStance ? { ...emptyDraft(), finalResponse: presetStance } : emptyDraft());
      });
    return () => { alive = false; };
  }, [taskId, partition, correction, presetStance]);

  const found = existing.state === 'found' ? existing.entry : null;
  const existingPending = Boolean(correction && found?.projection_pending);
  const linkRow = taskId && links ? links.links[taskId] : undefined;
  const selectedWorkflow = choice === null ? linkRow?.workflow_id ?? null : choice === '__none__' ? null : choice;
  const explanation = correction ? draft.why.trim() : reason.trim();
  const canSave = Boolean(taskId) && existing.state !== 'loading' && draftIsValid(draft, rules)
    && (!found || correction || reason.trim().length > 0) && !saving
    && (!correction || (!!links && !linksFailed && !!correction.decisionId && !correction.feedbackOnly
      && !existingPending && explanation.length <= 2000 && frozen.current?.feedbackOnly !== true));
  const canFeedbackOnly = Boolean(correction?.decisionId && taskId && explanation && explanation.length <= 2000
    && !saving && stage === 'initial' && frozen.current?.feedbackOnly !== false);

  const close = () => {
    if (correction && (stage !== 'initial' || reviewUncertain)) {
      setProblem('The correction has partly saved. Finish or confirm its remaining steps before closing.');
      return;
    }
    onClose();
  };

  const reloadConflict = async () => {
    if (!taskId || !frozen.current) return;
    try {
      if (entryConflict) {
        const fresh = await getCafCorpusEntry(taskId, partition);
        setExisting({ state: 'found', entry: fresh });
        frozen.current.guidance.expected_version = fresh.version ?? 0;
        frozen.current.guidance.reviewed_token = fresh.version ? undefined : fresh.compare_token;
        frozen.current.expectedGuidance.archive_override_refs =
          frozen.current.guidance.action === 'AUTO_ARCHIVE' && fresh.archive_override_refs === true;
        setEntryConflict(false);
      }
      if (linkConflict) {
        const fresh = await getExampleLinks();
        setLinks(fresh);
        frozen.current.linkRevision = fresh.links[taskId]?.revision ?? 0;
        if (!frozen.current.explicitLink) frozen.current.workflow = fresh.links[taskId]?.workflow_id ?? null;
        setLinkConflict(false);
      }
      setProblem('Latest saved state loaded. Your correction is kept; retry to apply it.');
    } catch { setProblem("Couldn't load the latest saved state. Try again."); }
  };

  const saveCorrection = async (feedbackOnly = false) => {
    if (!correction || !taskId || entryConflict || linkConflict
      || (frozen.current && frozen.current.feedbackOnly !== feedbackOnly)
      || (feedbackOnly ? !canFeedbackOnly : !canSave)) return;
    const expected = frozen.current?.guidance.expected_version ?? found?.version ?? 0;
    const write = draftToWrite(draft, claimFields, found, rules);
    let acked: string[] = [];
    if (!frozen.current && !feedbackOnly) {
      const checked = await gate.confirmOrWarn({ source: { kind: 'task', id: taskId }, sender: partition, action: write.action, final_response_stance: write.final_response_stance, workflow_id: selectedWorkflow ?? null });
      if (!checked.proceed) return;
      acked = checked.warnings;
    }
    const initial = frozen.current ?? {
      review: { decision_id: correction.decisionId, verdict: 'incorrect' as const,
        correction_text: explanation, source: 'staff_report' as const,
        expected_revision: correction.reviewRevision, idempotency_key: newIdempotencyKey('caf-review') },
      guidance: { task_id: taskId, action: write.action, note: explanation, ...stanceField(write),
        extraction_schema: write.extraction_schema,
        ...(write.categoryOverride ? { categoryOverride: write.categoryOverride } : {}),
        expected_version: expected,
        ...(acked.length ? { acknowledged_warnings: acked } : {}),
        ...(found && !found.version && found.compare_token ? { reviewed_token: found.compare_token } : {}),
        reason: explanation },
      expectedGuidance: { action: write.action, note: explanation, reason: explanation, final_response_stance: write.final_response_stance,
        extraction_schema: write.extraction_schema,
        ...(write.categoryOverride ? { categoryOverride: write.categoryOverride } : {}),
        archive_override_refs: write.action === 'AUTO_ARCHIVE' && found?.archive_override_refs === true },
      workflow: selectedWorkflow, linkRevision: linkRow?.revision ?? 0,
      explicitLink: canAutomate && !!links && choice !== null
        && (!linkRow || selectedWorkflow !== linkRow.workflow_id),
      wasExisting: Boolean(found),
      feedbackOnly,
    };
    frozen.current = initial;
    setSaving(true);
    setProblem('');
    let currentStage = stage;
    if (currentStage === 'initial') {
      const result = await runSave(() => recordCafDecisionReview(taskId, initial.review),
        { replay: () => recordCafDecisionReview(taskId, initial.review) });
      if (!result.ok || !result.value) {
        setSaving(false);
        const mayHaveSaved = result.ok || result.message === MSG_NOT_CONFIRMED;
        setReviewUncertain(mayHaveSaved);
        if (!mayHaveSaved) frozen.current = null;
        setProblem(`${mayHaveSaved ? 'Could not confirm feedback. Retry this correction here; keep this page open.' : result.message} No example or workflow was saved by this attempt.`);
        return;
      }
      setReviewUncertain(false);
      currentStage = 'review'; setStage('review'); onSaved(null);
    }
    if (feedbackOnly) {
      setSaving(false); toast.saved('Feedback saved.'); onClose(); return;
    }
    let saved = savedEntry.current ?? found;
    if (currentStage === 'review') {
      const readBack = async (): Promise<ReadBack<CafCorpusEntry>> => {
        try {
          const fresh = await getCafCorpusEntry(taskId, partition);
          if (fresh.id === taskId && fresh.version === expected + 1 && matchesReplacementGuidance(fresh, initial.expectedGuidance)
              && fresh.guidance_committed === true && !fresh.projection_pending) return { state: 'landed', value: fresh };
          return fresh.version === expected ? { state: 'not_landed' } : { state: 'unknown' };
        } catch (err) { return statusOf(err) === 404 && expected === 0 ? { state: 'not_landed' } : { state: 'unknown' }; }
      };
      const previousUncertain = uncertain === 'guidance';
      const probe = previousUncertain ? await readBack() : null;
      const result = probe?.state === 'landed' ? { ok: true as const, value: probe.value }
        : probe?.state === 'unknown' ? { ok: false as const, message: MSG_NOT_CONFIRMED }
          : await runSave(() => recordCorpusFromTaskGuidance(initial.guidance), { readBack, finish: (entry) => finishPublication(entry) });
      if (!result.ok || !result.value) {
        setSaving(false); setUncertain(result.ok ? 'guidance' : result.message === MSG_NOT_CONFIRMED ? 'guidance' : null);
        setEntryConflict(!result.ok && result.message.includes('Someone else changed'));
        const exampleStatus = result.ok ? 'unconfirmed'
          : result.message === MSG_NOT_CONFIRMED ? 'could not be confirmed' : result.message;
        setProblem(`Feedback saved; example ${exampleStatus}. Retry this correction here; keep this page open.`);
        return;
      }
      if (result.value.publication_status === 'partial' || result.value.guidance_committed !== true || result.value.projection_pending) {
        setSaving(false); setUncertain('guidance');
        setProblem('Feedback saved; the example is committed but its approval is still being confirmed. Retry this correction here; keep this page open.');
        onSaved(result.value);
        return;
      }
      saved = result.value;
      savedEntry.current = saved;
      setUncertain(null); currentStage = 'guidance'; setStage('guidance'); onSaved(saved);
    }
    if (currentStage === 'guidance' && initial.explicitLink) {
      const checkLink = async () => {
        const fresh = await getExampleLinks();
        const row = fresh.links[taskId];
        if (row?.revision === initial.linkRevision + 1 && row.workflow_id === initial.workflow) return 'landed';
        if ((row?.revision ?? 0) !== initial.linkRevision) return 'conflict';
        return 'not_landed';
      };
      let linkState = uncertain === 'link' ? await checkLink().catch(() => 'unknown') : 'not_landed';
      if (linkState === 'not_landed') {
        const result = await runSave(() => setExampleWorkflow(taskId, { sender: partition,
          workflow_id: initial.workflow, expected_revision: initial.linkRevision, note: explanation }));
        linkState = result.ok ? 'landed' : await checkLink().catch(() => 'unknown');
      }
      if (linkState !== 'landed') {
        setSaving(false); setUncertain(linkState === 'unknown' ? 'link' : null);
        setLinkConflict(linkState === 'conflict');
        setProblem(`Feedback and example saved; workflow ${linkState === 'conflict' ? 'changed by someone else' : 'unconfirmed'}. ${linkState === 'conflict' ? 'Reload saved state here before retrying.' : 'Retry this correction here; keep this page open.'}`);
        return;
      }
      currentStage = 'link'; setStage('link'); setUncertain(null);
    }
    if (!initial.explicitLink) {
      try {
        const current = (await getExampleLinks()).links[taskId];
        if ((current?.revision ?? 0) !== initial.linkRevision || (current?.workflow_id ?? null) !== initial.workflow) {
          setSaving(false); setLinkConflict(true);
          setProblem('Feedback and example saved; the workflow link changed meanwhile. Reload saved state here before continuing.');
          return;
        }
      } catch {
        setSaving(false); setUncertain('link');
        setProblem('Feedback and example saved; the existing workflow link could not be confirmed. Retry this correction here; keep this page open.');
        return;
      }
    }
    const workflowName = initial.workflow
      ? links?.workflows.find((item) => item.workflow_id === initial.workflow)?.name ?? initial.workflow : 'None';
    const confirmation = `${initial.wasExisting ? 'Updated' : 'Created'} this email's approved example. Workflow: ${workflowName}.`;
    try {
      const mode = await getAiSwitch();
      toast.saved(`${confirmation} ${mode.mode === 'watching' ? 'The AI is Watching; no action was taken on this email.' : `Current AI mode: ${mode.mode}.`}`);
    } catch { toast.saved(`${confirmation} Current AI mode could not be confirmed; check Automation.`); }
    setSaving(false); onSaved(saved); onClose();
  };

  const save = async () => {
    if (!canSave || !taskId) return;
    const expected = found ? found.version || 0 : 0;
    const write = draftToWrite(draft, claimFields, undefined, rules);
    const checked = await gate.confirmOrWarn({ source: { kind: 'task', id: taskId }, sender: partition, action: write.action, final_response_stance: write.final_response_stance });
    if (!checked.proceed) return;
    const payload: FromTaskPayload = {
      task_id: taskId,
      action: write.action,
      note: write.note,
      ...stanceField(write),
      extraction_schema: write.extraction_schema,
      ...(write.categoryOverride ? { categoryOverride: write.categoryOverride } : {}),
      expected_version: expected,
      ...(checked.warnings.length ? { acknowledged_warnings: checked.warnings } : {}),
      ...(found && !found.version && found.compare_token ? { reviewed_token: found.compare_token } : {}),
      reason: found ? reason.trim() : FIRST_LABEL_REASON,
    };
    setSaving(true);
    setProblem('');
    const result = await runSave<CafCorpusEntry>(
      () => Promise.resolve(recordCorpusFromTask(payload)).catch((err) => {
        // The task has gone from the live table (archived or removed): say so in plain words.
        if (statusOf(err) === 404) {
          throw Object.assign(new Error('email_not_available'), { response: { status: 404, data: { code: 'email_not_available' } } });
        }
        throw err;
      }),
      {
        readBack: async (): Promise<ReadBack<CafCorpusEntry>> => {
          try {
            const fresh = await getCafCorpusEntry(taskId, partition);
            const version = fresh.version ?? 0;
            if (version === expected + 1 && matchesReplacementGuidance(fresh, payload)) return { state: 'landed', value: fresh };
            return version === expected ? { state: 'not_landed' } : { state: 'unknown' };
          } catch (err) {
            return statusOf(err) === 404 && expected === 0 ? { state: 'not_landed' } : { state: 'unknown' };
          }
        },
        finish: (entry) => finishPublication(entry),
      },
    );
    setSaving(false);
    if (!result.ok) {
      setProblem(result.message);
      toast.failed(result.message, () => { void save(); });
      return;
    }
    const entry = result.value;
    onSaved(entry);
    toast.saved('Saved. This email is now an approved example.',
      entry && onShowExample ? { label: 'Show it', onClick: () => onShowExample(entry.id, entry.sender) } : undefined);
    onClose();
  };

  return (
    <Modal title={correction ? 'Correct this decision and teach the AI' : preset ? 'Teach the AI from this email' : 'Add one email as an example'} size="xwide" onClose={close} busy={saving}
      footer={(
        <>
          {problem && <div className="cur-alert cur-alert--error" role="alert"><span className="cur-grow">{problem}</span></div>}
          {(linkConflict || entryConflict) && <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" disabled={saving} onClick={() => { void reloadConflict(); }}>Reload saved state</button>}
          {correction && stage !== 'initial' && !uncertain && !reviewUncertain && <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" disabled={saving} onClick={() => {
            toast.info(stage === 'review' ? 'Feedback saved. The example was not confirmed by this correction.' : 'Feedback and example saved. The workflow link was not confirmed by this correction.');
            onClose();
          }}>Stop teaching here</button>}
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={close} disabled={saving}>Cancel</button>
          {correction && <button type="button" className={correction.feedbackOnly ? 'ui-btn ui-btn--primary ui-btn--md' : 'ui-btn ui-btn--secondary ui-btn--md'} disabled={!canFeedbackOnly} onClick={() => { void saveCorrection(true); }}>Save feedback only</button>}
          {!correction?.feedbackOnly && <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={!canSave || entryConflict || linkConflict} onClick={() => { void (correction ? saveCorrection() : save()); }}>
            {saving ? 'Saving…' : gate.conflicts.length ? 'Save anyway' : correction ? stage === 'review' ? 'Finish example and workflow' : stage === 'guidance' ? 'Finish workflow link' : 'Save correction and teach the AI' : 'Save as approved example'}
          </button>
          }
        </>
      )}>
      {preset
        ? <EmailPreviewPane preview={presetPreview} loading={!presetPreview} />
        : (
          <>
            <p className="cur-hint">Use this when one email should teach the AI on its own, without a group.</p>
            <EmailPicker mode="one" senders={senders} sender={sender} onSenderChange={setSender} picked={picked}
              onPickedChange={setPicked} disabled={saving} />
          </>
        )}
      {taskId && existing.state === 'loading' && <p className="cur-hint">Checking whether this email is already an example…</p>}
      {taskId && correction && existing.state === 'loading' && problem && <div className="cur-field">
        <label htmlFor="one-feedback-explanation">Explanation for feedback only</label>
        <textarea id="one-feedback-explanation" className="cur-input" rows={3} maxLength={2000} value={draft.why}
          disabled={saving || !!frozen.current} onChange={(event) => setDraft({ ...draft, why: event.target.value })} />
      </div>}
      {taskId && existing.state !== 'loading' && (
        <>
          {found ? <div className="cur-alert cur-alert--info" role="note">This email is already an approved example. Saving updates it.</div>
            : correction && <div className="cur-alert cur-alert--info" role="note">Saving creates an approved example for this email.</div>}
          {correction && <p className="cur-hint">{correction.feedbackOnly ? 'This email is archived. You can save feedback, but cannot teach from it until it is restored.' : 'This teaches future decisions. It does not repair or replay this email.'}</p>}
          <LabelForm idPrefix="one" value={draft} onChange={setDraft} claimFields={claimFields} allowArchiveWithRefs={false} disabled={saving || stage !== 'initial' || !!frozen.current} hideWhy={Boolean(correction)}
            afterAction={correction && links ? <div className="cur-field">
              {canAutomate ? <><label htmlFor="one-workflow">Workflow</label><select id="one-workflow" className="cur-input" value={choice ?? (linkRow ? linkRow.workflow_id ?? '__none__' : '__unchosen__')} disabled={saving || stage !== 'initial' || !!frozen.current} onChange={(event) => setChoice(event.target.value)}>
                {!linkRow && <option value="__unchosen__" disabled>No workflow chosen yet</option>}
                <option value="__none__">None</option>{links.workflows.map((item) => <option key={item.workflow_id} value={item.workflow_id}>{item.name} ({item.state})</option>)}
              </select>{!linkRow && <span className="cur-hint">Leave this unchosen to keep no workflow link, or choose None to record that choice.</span>}</> : <><strong>Workflow</strong><span>{links.workflows.find((item) => item.workflow_id === linkRow?.workflow_id)?.name ?? 'None'}</span><span className="cur-hint">Only an admin can change this.</span></>}
            </div> : undefined} />
          <ConflictWarnings conflicts={gate.conflicts} proposal={{ action: draft.action, final_response_stance: draft.finalResponse }} />
          {correction && <div className="cur-field"><label htmlFor="one-explanation">Explanation</label><textarea id="one-explanation" className="cur-input" rows={3} maxLength={2000} value={draft.why} disabled={saving || stage !== 'initial' || !!frozen.current} onChange={(event) => setDraft({ ...draft, why: event.target.value })} /></div>}
          {found && !correction && (
            <div className="cur-field">
              <label htmlFor="one-reason">What changed, and why?</label>
              <textarea id="one-reason" className="cur-input" rows={2} maxLength={2000} value={reason} disabled={saving}
                onChange={(event) => setReason(event.target.value)} />
            </div>
          )}
          {correction && linksFailed && <div role="alert">Couldn&apos;t load workflow links. Teaching is paused; feedback can be saved after a refresh.</div>}
          {existingPending && <div role="alert">This example has an earlier save still pending. Finish or review that save in Teach the AI before teaching from this email. Feedback can still be saved.</div>}
        </>
      )}
    </Modal>
  );
};
